import React from 'react';
import { Heart, MessageCircle, Bookmark, Music, Share2, Forward } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import type { PreviewProps } from './types';

// Helper function to check if URL is a video
const isVideo = (url?: string): boolean => {
  if (!url) return false;
  const extension = url.split('.').pop()?.toLowerCase();
  return ['mp4', 'mov', 'webm'].includes(extension || '');
};

export const TikTokPreview: React.FC<PreviewProps> = ({ content, brand, mediaUrl, format = 'regular' }) => {
  // Get the TikTok account from brand's social accounts if available
  const getTikTokAccount = () => {
    if (brand?.social_accounts && Array.isArray(brand.social_accounts)) {
      return brand.social_accounts.find(
        account => account.platform.toLowerCase() === 'tiktok'
      );
    }
    return null;
  };
  
  const tiktokAccount = getTikTokAccount();
  
  // Get the TikTok username
  const getTikTokUsername = (): string => {
    if (tiktokAccount?.username) {
      return tiktokAccount.username;
    }
    
    // Fallback to the old behavior if no TikTok account found
    return brand?.name || 'companyname';
  };
  
  // Get the profile picture
  const getProfilePicture = (): string => {
    if (tiktokAccount?.picture_url) {
      return tiktokAccount.picture_url;
    }
    
    // Fallback to the brand logo
    return brand?.logo || '';
  };

  // TikTok is primarily a short-form video platform, so we'll use the same format for both text and reel
  return (
    <div className="bg-black text-white rounded-xl shadow-md border border-gray-800 overflow-hidden max-w-[320px] mx-auto">
      <div className="relative w-full aspect-[9/16] bg-gray-900 flex flex-col">
        {/* Main content */}
        <div className="flex-1 relative">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="TikTok video"
            type="video"
            aspectRatio="9/16"
            key={mediaUrl}
          />
          
          {/* Right side actions */}
          <div className="absolute right-3 bottom-20 flex flex-col items-center space-y-5">
            <div className="flex flex-col items-center">
              <div className="h-10 w-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 mb-1">
                {getProfilePicture() ? (
                  <img src={getProfilePicture()} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center -mt-3 text-xs font-bold">+</div>
            </div>
            <button className="flex flex-col items-center">
              <Heart className="w-8 h-8" />
              <span className="text-xs mt-1">245.7K</span>
            </button>
            <button className="flex flex-col items-center">
              <MessageCircle className="w-8 h-8" />
              <span className="text-xs mt-1">1,893</span>
            </button>
            <button className="flex flex-col items-center">
              <Bookmark className="w-8 h-8" />
              <span className="text-xs mt-1">84.2K</span>
            </button>
            <button className="flex flex-col items-center">
              <Share2 className="w-8 h-8" />
              <span className="text-xs mt-1">5,621</span>
            </button>
            <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 mt-6 animate-[spin_8s_linear_infinite] flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-black"></div>
            </div>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold">@{getTikTokUsername()}</span>
            </div>
            <p className="mb-3 whitespace-pre-line" style={{ fontSize: '0.8rem' }}>{content}</p>
            <div className="flex items-center">
              <Music className="w-3 h-3 mr-2" />
              <div className="text-xs marquee-container overflow-hidden">
                <div className="animate-marquee whitespace-nowrap">
                  {brand?.name || 'companyname'} - Original Sound
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};